import { useLazyQuery } from "@apollo/client";
import cn from "classnames";
import Link from "next/link";
import { useRouter } from "next/router";
import { useTranslation } from "next-i18next";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";

import { GET_ALL_PAGES_USER } from "@api/queriesV2/cms";
import BlockTitle from "@components/cms/BlockTitle/BlockTitle";
import { getPageTypes } from "@config/cms";
import { useIsMounted } from "@hooks/useIsMounted";
import { useStore } from "@hooks/useZustandProvider";
import i from "@styles/icons.module.scss";
import Container from "@ui-kit/Container/Container";
import Loading from "@ui-kit/Loading/Loading";
import { localizedDate } from "@utils/utils";

import s from "./NewsListBlock.module.scss";

const NewsListBlock = ({ data, settings, updateField }) => {
  const isMounted = useIsMounted();
  const { t } = useTranslation();
  const { blockType } = settings.common;
  const { title } = data;
  const { getBlockLocale } = useStore();
  const { locale } = useRouter();
  const [newsList, setNewsList] = useState([]);
  const [pageNumber, setPageNumber] = useState(1);
  const [showMore, setShowMore] = useState(true);
  const PAGE_SIZE = 6;
  const lang = getBlockLocale(locale);

  const onLoadMore = () => {
    setPageNumber(pageNumber + 1);
  };

  const [getNews, { loading }] = useLazyQuery(GET_ALL_PAGES_USER, {
    context: { clientName: "v2" },
    variables: {
      language: lang,
      type: getPageTypes().news,
      pageNumber,
      pageSize: PAGE_SIZE,
    },
    onCompleted: (data) => {
      setNewsList((state) => [...state, ...data.getAllPagesUser]);
    },
    onError: (error) => {
      toast.error(`[${error.name}]: ${error.message}`);
    },
  });

  useEffect(() => {
    getNews();
  }, [pageNumber]);

  useEffect(() => {
    if (isMounted) {
      setNewsList([]);
    }
  }, [lang, isMounted]);

  useEffect(() => {
    setShowMore(pageNumber * PAGE_SIZE === newsList.length);
  }, [newsList.length]);

  return (
    <Container className={s.news}>
      <Container full={false}>
        <section>
          <BlockTitle
            settings={settings}
            field={{ title }}
            onChange={updateField}
            type={blockType}
          />
          {pageNumber === 1 && loading ? (
            <Loading />
          ) : newsList?.length ? (
            <div className={s.newsList}>
              {newsList.map((news, key) => {
                const { isPublished } = news;
                const { slug } = news.data;
                const { title } = news.data.seo;
                const { datePublished } = news.properties;
                return (
                  <Link key={key} href={`/news/${slug}`} className={s.item}>
                    <div className={s.date}>
                      {datePublished
                        ? localizedDate(datePublished, "dd MMMM yyyy", locale)
                        : "- - -"}
                      {!isPublished && (
                        <div className={!isPublished && s.unpublishedText}>
                          {t("unpublished")}
                        </div>
                      )}
                    </div>
                    <h5
                      className={cn(s.itemTitle, !isPublished && s.unpublished)}
                    >
                      {title}
                    </h5>
                  </Link>
                );
              })}
              {showMore && (
                <div className={s.showMoreWrap}>
                  {loading ? (
                    <Loading />
                  ) : (
                    <div className={s.showMore} onClick={onLoadMore}>
                      {t("show more")}
                      <icon className={i.arrowRight_v2} />
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className={s.newsList}>No news yet.</div>
          )}
        </section>
      </Container>
    </Container>
  );
};

export default NewsListBlock;
